import { useState, useEffect, useMemo } from "react";
import {
  Box,
  TextField,
  CircularProgress,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableFooter,
  Paper,
  Collapse,
  Chip,
  Stack,
  InputAdornment,
  IconButton,
  Button,
  TableSortLabel,
} from "@mui/material";
import KeyboardArrowRightIcon from "@mui/icons-material/KeyboardArrowRight";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import SearchIcon from "@mui/icons-material/Search";
import AppBreadcrumbs from "../../../components/AppBreadcrumbs";
import * as services from "../../../services/saleng";

export default function DailyPurchaseSummary() {
  const formatDate = (date) => date.toISOString().split("T")[0];
  const [startDate, setStartDate] = useState(() => {
    const date = new Date();
    date.setMonth(date.getMonth() - 1);
    return formatDate(date);
  });
  const [endDate, setEndDate] = useState(formatDate(new Date()));
  const [loading, setLoading] = useState(false);
  const [rows, setRows] = useState([]);
  const [openSet, setOpenSet] = useState(() => new Set());
  const [searchQuery, setSearchQuery] = useState("");

  // state สำหรับการ sort (JS ไม่มี type)
  const [orderBy, setOrderBy] = useState("name"); // name | personType | totalWeight | totalBeforeVat | totalVat | totalNet
  const [order, setOrder] = useState("asc"); // asc | desc

  const cellStyle = { border: "1px solid #ddd" };

  const mapPersonType = {
    normal: "บุคคลธรรมดา",
    company: "นิติบุคคล",
  };

  // ---- ดึงข้อมูล ----
  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const res = await services.getPurchaseTransactions({
          status: "payed",
          startDate,
          endDate,
        });

        const rawData = res.data?.data ?? [];
        const grouped = groupByCustomerAndMergeItems(rawData);
        setRows(grouped);
        setOpenSet(new Set());
      } catch (err) {
        console.error("fetch error", err);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [startDate, endDate]);

  // ---- Filter ----
  const filteredRows = useMemo(() => {
    const q = (searchQuery || "").toLowerCase();
    if (!q) return rows;

    return rows.filter((row) => {
      const name = row.customer?.name?.toLowerCase() || "";
      const typeLabel = mapPersonType[row.customer?.personType] || "";
      const type = typeLabel.toLowerCase();
      const carList = (row.carNames || []).join(" ").toLowerCase();

      return name.includes(q) || type.includes(q) || carList.includes(q);
    });
  }, [rows, searchQuery]);

  // รวมค่าต่อแถว (ใช้ซ้ำทั้งแสดงผล/คำนวณ sort)
  const getRowTotals = (row) => {
    const totalWeight = sum(row.items, "netWeight");
    const totalBeforeVat = sum(row.items, "amountBeforeVat");
    const totalVat = sum(row.items, "vat");
    const totalNet = sum(row.items, "netPrice");
    return { totalWeight, totalBeforeVat, totalVat, totalNet };
  };

  // ---- Sorted ----
  const sortedRows = useMemo(() => {
    const arr = [...filteredRows];

    const getSortValue = (row) => {
      const name = row.customer?.name ?? "";
      const personTypeLabel =
        mapPersonType[row.customer?.personType] ?? (row.customer?.personType ?? "");
      const totals = getRowTotals(row);

      switch (orderBy) {
        case "name":
          return String(name).toLowerCase();
        case "personType":
          return String(personTypeLabel).toLowerCase();
        case "totalWeight":
          return Number(totals.totalWeight || 0);
        case "totalBeforeVat":
          return Number(totals.totalBeforeVat || 0);
        case "totalVat":
          return Number(totals.totalVat || 0);
        case "totalNet":
          return Number(totals.totalNet || 0);
        default:
          return "";
      }
    };

    arr.sort((a, b) => {
      const va = getSortValue(a);
      const vb = getSortValue(b);

      if (typeof va === "number" && typeof vb === "number") {
        return order === "asc" ? va - vb : vb - va;
      }

      const sa = String(va);
      const sb = String(vb);
      return order === "asc" ? sa.localeCompare(sb) : sb.localeCompare(sa);
    });

    return arr;
  }, [filteredRows, orderBy, order]);

  // ---- Summary จาก filteredRows ----
  const summary = useMemo(() => {
    let customerCount = filteredRows.length;
    let totalWeight = 0;
    let totalBeforeVat = 0;
    let totalVat = 0;
    let totalNet = 0;

    filteredRows.forEach((row) => {
      (row.items ?? []).forEach((i) => {
        totalWeight += i.netWeight ?? 0;
        totalBeforeVat += i.amountBeforeVat ?? 0;
        totalVat += i.vat ?? 0;
        totalNet += i.netPrice ?? 0;
      });
    });

    return { customerCount, totalWeight, totalBeforeVat, totalVat, totalNet };
  }, [filteredRows]);

  const getRowKey = (row, idx) =>
    String(row.customer?.id ?? row.customerId ?? idx);

  const toggleOpen = (key) => {
    setOpenSet((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  // --- CSV helper ---
  function exportToCSV(data, filename = "export.csv") {
    if (!data || data.length === 0) return;
    const headers = Object.keys(data[0]);
    const rows = data.map((row) =>
      headers.map((field) => `"${row[field] ?? ""}"`).join(",")
    );
    const csv = [headers.join(","), ...rows].join("\n");
    const blob = new Blob(["\uFEFF" + csv], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.setAttribute("download", filename);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }

  // --- Export: สรุปรายซัพพลายเออร์ (อิงลำดับที่ sort แล้ว) ---
  const handleExportSummary = () => {
    const dataset = sortedRows.map((row) => {
      const { totalWeight, totalBeforeVat, totalVat, totalNet } = getRowTotals(row);
      const avgPrice = avg(row.items, "pricePerUnit");

      return {
        รายการ: row.customer?.name ?? "-",
        ประเภทซัพพลายเออร์:
          row.customer?.personType === "company"
            ? "นิติบุคคล"
            : row.customer?.personType === "normal"
            ? "บุคคลธรรมดา"
            : "-",
        รถขนส่ง: (row.carNames || []).join(" | "),
        น้ำหนัก: Number(totalWeight).toFixed(2),
        หน่วย: "กก.",
        ราคาต่อหน่วย: Number(avgPrice).toFixed(2),
        ยอดก่อนรวมภาษี: Number(totalBeforeVat).toFixed(2),
        ยอดภาษี: Number(totalVat).toFixed(2),
        ยอดสุทธิ: Number(totalNet).toFixed(2),
      };
    });

    exportToCSV(dataset, `purchase_summary_by_customer_${startDate}_to_${endDate}.csv`);
  };

  // --- Export: รายการย่อยทั้งหมด ---
  const handleExportAllItems = () => {
    const dataset = [];
    sortedRows.forEach((row) => {
      const common = {
        ซัพพลายเออร์: row.customer?.name ?? "-",
        ประเภทซัพพลายเออร์:
          row.customer?.personType === "company"
            ? "นิติบุคคล"
            : row.customer?.personType === "normal"
            ? "บุคคลธรรมดา"
            : "-",
        รถขนส่ง: (row.carNames || []).join(" | "),
      };

      (row.items || []).forEach((it) => {
        dataset.push({
          ...common,
          สินค้า: it.product?.title ?? "-",
          น้ำหนัก: Number(it.netWeight || 0).toFixed(2),
          หน่วย: it.unit ?? "กก.",
          ราคาต่อหน่วย: Number(it.pricePerUnit || 0).toFixed(2),
          ยอดก่อนรวมภาษี: Number(it.amountBeforeVat || 0).toFixed(2),
          ยอดภาษี: Number(it.vat || 0).toFixed(2),
          ยอดสุทธิ: Number(it.netPrice || 0).toFixed(2),
        });
      });
    });

    if (dataset.length === 0) return;
    exportToCSV(dataset, `purchase_all_items_${startDate}_to_${endDate}.csv`);
  };

  // เปลี่ยน sort เมื่อกดหัวคอลัมน์
  const handleSort = (key) => {
    setOrder((prev) => (orderBy === key ? (prev === "asc" ? "desc" : "asc") : "asc"));
    setOrderBy(key);
  };

  return (
    <Box sx={{ p: 2, backgroundColor: "#f9fafb", minHeight: "100vh" }}>
      <AppBreadcrumbs />
      <Box display="flex" justifyContent="flex-end" gap={2} my={2}>
        <TextField
          label="ค้นหา"
          value={searchQuery}
          placeholder="ค้นหาด้วยชื่อรายการ ประเภทหรือรถ"
          onChange={(e) => setSearchQuery(e.target.value)}
          size="medium"
          fullWidth
          sx={{ maxWidth: 400 }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
          }}
        />

        <TextField
          label="เริ่มต้น"
          type="date"
          value={startDate}
          onChange={(e) => setStartDate(e.target.value)}
          InputLabelProps={{ shrink: true }}
        />
        <TextField
          label="สิ้นสุด"
          type="date"
          value={endDate}
          onChange={(e) => setEndDate(e.target.value)}
          InputLabelProps={{ shrink: true }}
        />
      </Box>

      {loading ? (
        <Box display="flex" justifyContent="center" mt={5}>
          <CircularProgress />
        </Box>
      ) : (
        <>
          <Box display="flex" justifyContent="space-between" alignItems="center" my={1}>
            <Typography variant="h6">สรุปยอดซื้อสินค้ารายบุคคล</Typography>
            <Box display="flex" gap={1}>
              <Button
                variant="outlined"
                size="large"
                startIcon={<ContentCopyIcon />}
                onClick={handleExportSummary}
              >
                ข้อมูลหลัก
              </Button>
              <Button
                variant="outlined"
                size="large"
                startIcon={<ContentCopyIcon />}
                onClick={handleExportAllItems}
              >
                ข้อมูลย่อยทั้งหมด
              </Button>
            </Box>
          </Box>
          <TableContainer component={Paper}>
            <Table>
              <TableHead sx={{ backgroundColor: "#f0f0f0" }}>
                <TableRow>
                  <TableCell sx={{ ...cellStyle, width: 48 }} />
                  <TableCell sx={{ ...cellStyle, color: "primary.main" }}>
                    <TableSortLabel
                      active={orderBy === "name"}
                      direction={orderBy === "name" ? order : "asc"}
                      onClick={() => handleSort("name")}
                    >
                      รายการ
                    </TableSortLabel>
                  </TableCell>
                  <TableCell sx={{ ...cellStyle, color: "primary.main" }}>
                    <TableSortLabel
                      active={orderBy === "personType"}
                      direction={orderBy === "personType" ? order : "asc"}
                      onClick={() => handleSort("personType")}
                    >
                      ประเภทซัพพลายเออร์
                    </TableSortLabel>
                  </TableCell>
                  <TableCell sx={cellStyle}>รถขนส่ง</TableCell>
                  <TableCell align="right" sx={{ ...cellStyle, color: "primary.main" }}>
                    <TableSortLabel
                      active={orderBy === "totalWeight"}
                      direction={orderBy === "totalWeight" ? order : "asc"}
                      onClick={() => handleSort("totalWeight")}
                    >
                      น้ำหนัก
                    </TableSortLabel>
                  </TableCell>
                  <TableCell sx={cellStyle}>หน่วย</TableCell>
                  <TableCell align="right" sx={cellStyle}>
                    ราคาต่อหน่วย
                  </TableCell>
                  <TableCell align="right" sx={{ ...cellStyle, color: "primary.main" }}>
                    <TableSortLabel
                      active={orderBy === "totalBeforeVat"}
                      direction={orderBy === "totalBeforeVat" ? order : "asc"}
                      onClick={() => handleSort("totalBeforeVat")}
                    >
                      ยอดก่อนรวมภาษี
                    </TableSortLabel>
                  </TableCell>
                  <TableCell align="right" sx={{ ...cellStyle, color: "primary.main" }}>
                    <TableSortLabel
                      active={orderBy === "totalVat"}
                      direction={orderBy === "totalVat" ? order : "asc"}
                      onClick={() => handleSort("totalVat")}
                    >
                      ยอดภาษี
                    </TableSortLabel>
                  </TableCell>
                  <TableCell align="right" sx={{ ...cellStyle, color: "primary.main" }}>
                    <TableSortLabel
                      active={orderBy === "totalNet"}
                      direction={orderBy === "totalNet" ? order : "asc"}
                      onClick={() => handleSort("totalNet")}
                    >
                      ยอดสุทธิ
                    </TableSortLabel>
                  </TableCell>
                </TableRow>
              </TableHead>

              <TableBody>
                {sortedRows.map((row, idx) => {
                  const key = getRowKey(row, idx);
                  const isOpen = openSet.has(key);
                  return (
                    <RowGroup
                      key={key}
                      isOpen={isOpen}
                      onToggle={() => toggleOpen(key)}
                      row={row}
                      cellStyle={cellStyle}
                    />
                  );
                })}
              </TableBody>

              <TableFooter>
                <TableRow
                  sx={{
                    backgroundColor: "#fafafa",
                    "& td": { fontSize: "14px", fontWeight: "bold" },
                  }}
                >
                  <TableCell sx={cellStyle} />
                  <TableCell sx={cellStyle} colSpan={3}>
                    รวมซัพพลายเออร์ {summary.customerCount.toLocaleString("th-TH")} ราย
                  </TableCell>

                  <TableCell align="right" sx={cellStyle}>
                    {formatNum(summary.totalWeight)}
                  </TableCell>
                  <TableCell sx={cellStyle}></TableCell>
                  <TableCell sx={cellStyle}></TableCell>
                  <TableCell align="right" sx={cellStyle}>
                    {formatNum(summary.totalBeforeVat)}
                  </TableCell>
                  <TableCell align="right" sx={cellStyle}>
                    {formatNum(summary.totalVat)}
                  </TableCell>
                  <TableCell align="right" sx={cellStyle}>
                    {formatNum(summary.totalNet)}
                  </TableCell>
                </TableRow>
              </TableFooter>
            </Table>
          </TableContainer>
        </>
      )}
    </Box>
  );
}

/** แถวหลัก + รายการย่อย */
function RowGroup({ isOpen, onToggle, row, cellStyle }) {
  const totalWeight = sum(row.items, "netWeight");
  const avgPrice = avg(row.items, "pricePerUnit");
  const totalBeforeVat = sum(row.items, "amountBeforeVat");
  const totalVat = sum(row.items, "vat");
  const totalNet = sum(row.items, "netPrice");

  return (
    <>
      <TableRow hover>
        <TableCell sx={{ ...cellStyle, width: 48 }}>
          <IconButton size="small" onClick={onToggle} aria-label="expand row">
            {isOpen ? <KeyboardArrowDownIcon /> : <KeyboardArrowRightIcon />}
          </IconButton>
        </TableCell>

        <TableCell sx={cellStyle}>{row.customer?.name ?? "-"}</TableCell>
        <TableCell sx={cellStyle}>
          {row.customer?.personType === "company"
            ? "นิติบุคคล"
            : row.customer?.personType === "normal"
            ? "บุคคลธรรมดา"
            : "-"}
        </TableCell>
        <TableCell sx={cellStyle}>
          <Stack direction="row" flexWrap="wrap" gap={1}>
            {(row.carNames || []).map((car, i) => (
              <Chip key={i} label={car} size="small" />
            ))}
          </Stack>
        </TableCell>

        <TableCell align="right" sx={cellStyle}>
          {formatNum(totalWeight)}
        </TableCell>
        <TableCell sx={cellStyle}>กก.</TableCell>
        <TableCell align="right" sx={cellStyle}>
          {formatNum(avgPrice)}
        </TableCell>
        <TableCell align="right" sx={cellStyle}>
          {formatNum(totalBeforeVat)}
        </TableCell>
        <TableCell align="right" sx={cellStyle}>
          {formatNum(totalVat)}
        </TableCell>
        <TableCell align="right" sx={cellStyle}>
          {formatNum(totalNet)}
        </TableCell>
      </TableRow>

      <TableRow>
        <TableCell style={{ padding: 0 }} colSpan={10}>
          <Collapse in={isOpen} timeout="auto" unmountOnExit>
            <Box p={2}>
              <Typography variant="subtitle2" mb={1}>
                รายการสินค้า
              </Typography>
              <Table>
                <TableHead sx={{ backgroundColor: "#f0f0f0" }}>
                  <TableRow>
                    <TableCell sx={cellStyle}>สินค้า</TableCell>
                    <TableCell align="right" sx={cellStyle}>
                      น้ำหนัก
                    </TableCell>
                    <TableCell sx={cellStyle}>หน่วย</TableCell>
                    <TableCell align="right" sx={cellStyle}>
                      ราคาซื้อ/หน่วย
                    </TableCell>
                    <TableCell align="right" sx={cellStyle}>
                      ยอดก่อนภาษี
                    </TableCell>
                    <TableCell align="right" sx={cellStyle}>
                      ภาษี
                    </TableCell>
                    <TableCell align="right" sx={cellStyle}>
                      ยอดรวม
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {(row.items || []).map((p, i) => (
                    <TableRow key={i}>
                      <TableCell sx={cellStyle}>
                        {p.product?.title ?? "-"}
                      </TableCell>
                      <TableCell align="right" sx={cellStyle}>
                        {formatNum(p.netWeight)}
                      </TableCell>
                      <TableCell sx={cellStyle}>{p.unit ?? "กก."}</TableCell>
                      <TableCell align="right" sx={cellStyle}>
                        {formatNum(p.pricePerUnit)}
                      </TableCell>
                      <TableCell align="right" sx={cellStyle}>
                        {formatNum(p.amountBeforeVat)}
                      </TableCell>
                      <TableCell align="right" sx={cellStyle}>
                        {formatNum(p.vat)}
                      </TableCell>
                      <TableCell align="right" sx={cellStyle}>
                        {formatNum(p.netPrice)}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Box>
          </Collapse>
        </TableCell>
      </TableRow>
    </>
  );
}

/** ---------- Utils ---------- */

function groupByCustomerAndMergeItems(data = []) {
  const grouped = {};

  data.forEach((row) => {
    const cid = row.customerId ?? row.customer?.id ?? "__unknown__";
    if (!grouped[cid]) {
      grouped[cid] = {
        customerId: cid,
        customer: row.customer,
        carNames: [],
        items: [],
      };
    }

    const carName = row.car?.license
      ? `${row.car?.type || ""} ${row.car.license}`.trim()
      : "ไม่มีข้อมูล";
    if (carName && !grouped[cid].carNames.includes(carName)) {
      grouped[cid].carNames.push(carName);
    }

    const isVat = row.isVat ?? false;
    const items = (row.items ?? []).map((item) => {
      const netWeight = item.netWeight || 0;
      const pricePerUnit = item.pricePerUnit || 0;
      const amountBeforeVat = netWeight * pricePerUnit;
      const vat = isVat ? amountBeforeVat * 0.07 : 0;
      const netPrice = amountBeforeVat + vat;

      return {
        ...item,
        netWeight,
        pricePerUnit,
        unit: item.unit || "กก.",
        amountBeforeVat,
        vat,
        netPrice,
      };
    });

    grouped[cid].items.push(...items);
  });

  // รวม item ที่ชื่อสินค้าซ้ำกัน
  return Object.values(grouped).map((entry) => {
    const merged = {};
    (entry.items || []).forEach((item) => {
      const key = item.product?.title ?? "-";
      if (!merged[key]) {
        merged[key] = { ...item };
      } else {
        merged[key].netWeight += item.netWeight;
        merged[key].amountBeforeVat += item.amountBeforeVat;
        merged[key].vat += item.vat;
        merged[key].netPrice += item.netPrice;
      }
    });

    return {
      ...entry,
      items: Object.values(merged),
    };
  });
}

function formatNum(val) {
  return val != null
    ? Number(val).toLocaleString("th-TH", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      })
    : "-";
}

function sum(arr, key) {
  return (arr || []).reduce((acc, item) => acc + (item?.[key] || 0), 0);
}

function avg(arr, key) {
  const a = arr || [];
  const total = a.reduce((acc, item) => acc + (item?.[key] || 0), 0);
  return a.length ? total / a.length : 0;
}
